import { useEffect, useId, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient, axiosClient } from '../../auth-lib';
import OrgAdminLayout from '../components/OrgAdminLayout';
import { Button, Card, Input, Select, Skeleton, useToast } from '../../design-system';
import GstinFields, { emptyGstin } from '../components/client/GstinFields';
import AddressFields, { emptyAddress } from '../components/client/AddressFields';
import GstPortalFetch from '../components/client/GstPortalFetch';
import GstinManager from '../components/client/GstinManager';
import ClientAuditorsManager from '../components/client/ClientAuditorsManager';
import ClientPortalUsers from '../components/ClientPortalUsers';
import BillingRecipients from '../components/finance/BillingRecipients';
import CredentialsManager from '../components/client/CredentialsManager';
import DocumentsManager from '../components/DocumentsManager';
import GovernanceFields, { hydrateGovernance, cleanGovernance } from '../components/client/GovernanceFields';
import PersonPicker from '../components/client/PersonPicker';
import { collectClientPeople, panFromGstins } from '../../lib/clientPeople';
import OrgChartFields, { hydrateOrgChart, cleanOrgChart } from '../components/client/OrgChartFields';
import '../styles/CreateEditClient.css';
import '../styles/ClientDetail.css'; // shared field-group styles (address-fieldset, apob-*)

const CLIENT_TYPES = [
  { value: 'individual', label: 'Individual' },
  { value: 'proprietorship', label: 'Proprietorship' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'llp', label: 'LLP' },
  { value: 'private_ltd', label: 'Private Ltd' },
  { value: 'public_ltd', label: 'Public Ltd' },
  { value: 'huf', label: 'HUF' },
  { value: 'trust', label: 'Trust' },
  { value: 'society', label: 'Society' },
];

// Format a Date as the local "YYYY-MM-DDTHH:mm" a <input type="datetime-local">
// expects (its value is timezone-naive, so we build it from local parts).
const toLocalDatetimeInput = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// ISO/Date -> the "YYYY-MM-DD" an <input type="date"> wants. '' for empty.
const toDateInput = (v) => (v ? new Date(v).toISOString().slice(0, 10) : '');
// Number|null -> string for a controlled input ('' for null/undefined).
const numToInput = (v) => (v == null ? '' : String(v));

// Ownership & group-structure vocab — mirrors the enums in models/Client.js.
const HOLDER_TYPES = [
  { value: 'individual', label: 'Individual' },
  { value: 'corporate', label: 'Corporate' },
  { value: 'trust', label: 'Trust' },
  { value: 'fund', label: 'Fund' },
];
const SHARE_CLASSES = [
  { value: 'equity', label: 'Equity' },
  { value: 'preference', label: 'Preference' },
  { value: 'ordinary', label: 'Ordinary' },
  { value: 'founder', label: 'Founder' },
  { value: 'other', label: 'Other' },
];
const GROUP_RELATION_TYPES = [
  { value: 'subsidiary', label: 'Subsidiary' },
  { value: 'associate', label: 'Associate' },
  { value: 'jv', label: 'Joint Venture' },
  { value: 'branch', label: 'Branch' },
];

const emptyOwner = {
  holderType: 'individual', name: '', holderClientId: '', pan: '', din: '', aadhaar: '',
  shareClass: 'equity', sharesHeld: '', ownershipPct: '',
  // Subscriber to the memorandum, and for how many shares. Frozen at
  // incorporation — NOT the same number as sharesHeld, which is today's.
  isSubscriber: false, subscribedShares: '',
  isUbo: false, acquiredOn: '', disposedOn: '',
};
const emptyGroupRelation = { childClientId: '', relationType: 'subsidiary', ownershipPct: '', effectiveFrom: '', effectiveTo: '' };

// MCA master data — mirrors COMPANY_CATEGORIES / COMPANY_SUB_CATEGORIES and
// CHARGE_TYPES in models/Client.js. Straight off the MCA company master.
const COMPANY_CATEGORIES = [
  { value: 'company_limited_by_shares', label: 'Company limited by shares' },
  { value: 'company_limited_by_guarantee', label: 'Company limited by guarantee' },
  { value: 'unlimited_company', label: 'Unlimited company' },
];
const COMPANY_SUB_CATEGORIES = [
  { value: 'non_govt_company', label: 'Non-govt company' },
  { value: 'state_govt_company', label: 'State govt company' },
  { value: 'union_govt_company', label: 'Union govt company' },
  { value: 'subsidiary_of_foreign_company', label: 'Subsidiary of a foreign company' },
  { value: 'guarantee_and_association_company', label: 'Guarantee and association company' },
];
const CHARGE_TYPES = [
  { value: 'hypothecation', label: 'Hypothecation' },
  { value: 'mortgage', label: 'Mortgage' },
  { value: 'pledge', label: 'Pledge' },
  { value: 'lien', label: 'Lien' },
  { value: 'assignment', label: 'Assignment' },
  { value: 'other', label: 'Other' },
];

const emptyCharge = {
  chargeId: '', holderName: '', chargeType: 'hypothecation', amount: '',
  propertyDescription: '', createdOn: '', modifiedOn: '', satisfiedOn: '', notes: '',
};

const emptyContact = { name: '', role: '', email: '', phone: '', isPrimary: false };
const emptyBankAccount = { accountNo: '', ifsc: '', bankName: '', branch: '', accountType: 'current', isPrimary: false };
const emptySignatory = { name: '', role: '', din: '', pan: '', aadhaar: '' };
const emptyTurnover = { fy: '', amount: '' };

function CreateEditClient() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const isEdit = Boolean(id);
  // Unique per instance — see the note beside the <datalist> below.
  const signatoryPeopleListId = useId();

  const [orgUsers, setOrgUsers] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // ── Onboarding is two steps; editing is not ────────────────────────────────
  // Step 1 is the four things a client cannot usefully exist without — who they
  // are, their registrations, and who at the firm owns them. Everything else
  // (cap table, governance, org chart, contacts, banks, signatories, turnover)
  // is step 2, is entirely optional, and can be skipped outright: "Create
  // Client" on step 1 is a complete onboarding, not a shortcut that leaves the
  // record half-made.
  //
  // Editing renders every section on one page as before. A wizard is right for
  // onboarding, where the order is a guided path; it is wrong for editing,
  // where somebody has come to change one known field and hiding it behind a
  // step would be an obstacle.
  const [step, setStep] = useState(1);
  const showEssentials = isEdit || step === 1;
  const showMoreDetails = isEdit || step === 2;
  // Auditor appointments are saved through their own endpoints the moment they
  // are made (they consume an audit slot and are ceiling-checked server-side),
  // so they are NOT part of formData and don't wait for "Save Changes".
  const [appointments, setAppointments] = useState([]);

  const [formData, setFormData] = useState({
    displayName: '',
    legalName: '',
    clientType: 'individual',
    status: 'onboarding',
    pan: '',
    tan: '',
    cin: '',
    llpin: '',
    iec: '',
    udyam: '',
    pf: '',
    esic: '',
    professionalTax: '',
    lwf: '',
    vatTin: '',
    // KYC. Mobile and email are NOT here — they live on contacts[], which is
    // already where the rest of the app reads a client's phone from.
    aadhaar: '',
    dateOfBirth: '',
    photoDocumentId: '',
    // Incorporation & MCA master data. dateOfIncorporation is a fact about the
    // client; onboardingDate below is when the firm took them on.
    dateOfIncorporation: '',
    companyCategory: '',
    companySubCategory: '',
    rocCode: '',
    nicCode: '',
    businessActivity: '',
    assignedPartnerId: '',
    assignedManagerId: '',
    financialYearEnd: '03-31',
    // Ownership & group structure (Phase 1). Scalars live here; owners[] and
    // groupRelations[] are their own state below (repeaters).
    parentClientId: '',
    authorisedCapital: '',
    paidUpCapital: '',
    // Display-only. Shown as browser-local time as a fallback until the live
    // server time from /api/time lands (create) or the record loads (edit).
    // Never sent to the server — onboarding time is stamped server-side.
    onboardingDate: toLocalDatetimeInput(new Date()),
  });
  const [contacts, setContacts] = useState([{ ...emptyContact, isPrimary: true }]);
  const [gstins, setGstins] = useState([]);
  // The registered office. Always an object (never null) so the fieldset stays
  // a controlled component; it is only SENT when something was actually typed.
  const [registeredAddress, setRegisteredAddress] = useState(emptyAddress());
  const [charges, setCharges] = useState([]);
  // The client's uploaded photographs, for the KYC photo picker. Edit only —
  // a document cannot belong to a client that does not exist yet.
  const [photoDocs, setPhotoDocs] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [signatories, setSignatories] = useState([]);
  const [turnoverByFY, setTurnoverByFY] = useState([]);
  const [owners, setOwners] = useState([]);
  const [groupRelations, setGroupRelations] = useState([]);
  const [board, setBoard] = useState([]);
  const [committees, setCommittees] = useState([]);
  const [statutoryOfficers, setStatutoryOfficers] = useState([]);
  const [orgUnits, setOrgUnits] = useState([]);
  const [employees, setEmployees] = useState([]);
  // Other clients in the org — the pick-list for corporate holders, the parent,
  // and group-relation children. Excludes this client (a client can't own itself).
  const [otherClients, setOtherClients] = useState([]);
  const [gstFetchOpen, setGstFetchOpen] = useState(false);

  useEffect(() => {
    axiosClient.get('/users').then((res) => setOrgUsers(res.data)).catch(() => {});
    // Pick-list for ownership: every other client in the org (self excluded).
    apiClient
      .get('/api/clients')
      .then((res) => setOtherClients(res.data.filter((c) => c._id !== id)))
      .catch(() => {});
    if (isEdit) {
      fetchClient();
      // The photo picker offers what is already in the vault rather than
      // adding a second upload path: a KYC photograph is an ordinary
      // Document, with the same storage, backup and access log as everything
      // else the firm holds.
      apiClient
        .get('/api/documents', { params: { clientId: id, category: 'PHOTO' } })
        .then((res) => setPhotoDocs(res.data || []))
        .catch(() => {});
    } else {
      // Stamp onboarding time from the live server clock, not this browser's.
      // The initial state holds local time only as a fallback until this lands.
      apiClient
        .get('/api/time')
        .then((res) => setFormData((prev) => ({ ...prev, onboardingDate: toLocalDatetimeInput(new Date(res.data.now)) })))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchClient = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/api/clients/${id}`);
      const c = res.data;
      setFormData({
        displayName: c.displayName || '',
        legalName: c.legalName || '',
        clientType: c.clientType || 'individual',
        status: c.status || 'onboarding',
        pan: c.pan || '',
        tan: c.tan || '',
        cin: c.cin || '',
        llpin: c.llpin || '',
        iec: c.iec || '',
        udyam: c.udyam || '',
        pf: c.pf || '',
        esic: c.esic || '',
        professionalTax: c.professionalTax || '',
        lwf: c.lwf || '',
        vatTin: c.vatTin || '',
        aadhaar: c.aadhaar || '',
        dateOfBirth: toDateInput(c.dateOfBirth),
        photoDocumentId: c.photoDocumentId?._id || c.photoDocumentId || '',
        dateOfIncorporation: toDateInput(c.dateOfIncorporation),
        companyCategory: c.companyCategory || '',
        companySubCategory: c.companySubCategory || '',
        rocCode: c.rocCode || '',
        nicCode: c.nicCode || '',
        businessActivity: c.businessActivity || '',
        assignedPartnerId: c.assignedPartnerId?._id || '',
        assignedManagerId: c.assignedManagerId?._id || '',
        financialYearEnd: c.financialYearEnd || '03-31',
        parentClientId: c.parentClientId?._id || c.parentClientId || '',
        authorisedCapital: numToInput(c.authorisedCapital),
        paidUpCapital: numToInput(c.paidUpCapital),
        onboardingDate: c.onboardingDate ? toLocalDatetimeInput(new Date(c.onboardingDate)) : '',
      });
      setContacts(c.contacts?.length ? c.contacts : [{ ...emptyContact, isPrimary: true }]);
      setGstins(c.gstins || []);
      setRegisteredAddress({ ...emptyAddress(), ...(c.registeredAddress || {}) });
      setCharges(
        (c.charges || []).map((ch) => ({
          ...emptyCharge,
          ...ch,
          amount: numToInput(ch.amount),
          createdOn: toDateInput(ch.createdOn),
          modifiedOn: toDateInput(ch.modifiedOn),
          satisfiedOn: toDateInput(ch.satisfiedOn),
        }))
      );
      setAppointments(c.auditors || []);
      // Hydrate ownership repeaters — numbers/dates to input-friendly strings.
      setOwners(
        (c.owners || []).map((o) => ({
          ...emptyOwner,
          ...o,
          holderClientId: o.holderClientId?._id || o.holderClientId || '',
          sharesHeld: numToInput(o.sharesHeld),
          subscribedShares: numToInput(o.subscribedShares),
          ownershipPct: numToInput(o.ownershipPct),
          acquiredOn: toDateInput(o.acquiredOn),
          disposedOn: toDateInput(o.disposedOn),
        }))
      );
      setGroupRelations(
        (c.groupRelations || []).map((g) => ({
          ...emptyGroupRelation,
          ...g,
          childClientId: g.childClientId?._id || g.childClientId || '',
          ownershipPct: numToInput(g.ownershipPct),
          effectiveFrom: toDateInput(g.effectiveFrom),
          effectiveTo: toDateInput(g.effectiveTo),
        }))
      );
      // Governance (Phase 2) — dates to input-friendly strings.
      const gov = hydrateGovernance(c);
      setBoard(gov.board);
      setCommittees(gov.committees);
      setStatutoryOfficers(gov.statutoryOfficers);
      // Org chart (Phase 3).
      const oc = hydrateOrgChart(c);
      setOrgUnits(oc.orgUnits);
      setEmployees(oc.employees);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load client');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleContactChange = (idx, field, value) => {
    setContacts((prev) =>
      prev.map((c, i) => {
        if (i !== idx) return field === 'isPrimary' && value ? { ...c, isPrimary: false } : c;
        return { ...c, [field]: value };
      })
    );
  };

  const addContact = () => setContacts((prev) => [...prev, { ...emptyContact }]);
  const removeContact = (idx) => setContacts((prev) => prev.filter((_, i) => i !== idx));

  // <GstinFields> hands back the whole updated gstin object.
  const setGstinAt = (idx, next) => setGstins((prev) => prev.map((g, i) => (i === idx ? next : g)));
  const addGstin = () => setGstins((prev) => [...prev, emptyGstin()]);
  const removeGstin = (idx) => setGstins((prev) => prev.filter((_, i) => i !== idx));

  // Fetched from the GST portal: append the GSTIN, and fill any client-level
  // field the portal can answer — but only where the user hasn't typed
  // something already, so we never overwrite their input.
  const handleGstFetched = (fetchedGstin, suggested) => {
    setGstins((prev) => [...prev, { ...emptyGstin(), ...fetchedGstin }]);
    setFormData((prev) => ({
      ...prev,
      pan: prev.pan || suggested?.pan || '',
      legalName: prev.legalName || suggested?.legalName || '',
      displayName: prev.displayName || suggested?.displayName || '',
    }));
    toast.success('GSTIN details fetched from the portal');
  };

  const handleBankChange = (idx, field, value) => {
    setBankAccounts((prev) =>
      prev.map((b, i) => {
        if (i !== idx) return field === 'isPrimary' && value ? { ...b, isPrimary: false } : b;
        return { ...b, [field]: value };
      })
    );
  };
  const addBank = () => setBankAccounts((prev) => [...prev, { ...emptyBankAccount }]);
  const removeBank = (idx) => setBankAccounts((prev) => prev.filter((_, i) => i !== idx));

  const handleSignatoryChange = (idx, field, value) =>
    setSignatories((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  const addSignatory = () => setSignatories((prev) => [...prev, { ...emptySignatory }]);
  const removeSignatory = (idx) => setSignatories((prev) => prev.filter((_, i) => i !== idx));

  const handleTurnoverChange = (idx, field, value) =>
    setTurnoverByFY((prev) => prev.map((t, i) => (i === idx ? { ...t, [field]: value } : t)));
  const addTurnover = () => setTurnoverByFY((prev) => [...prev, { ...emptyTurnover }]);
  const removeTurnover = (idx) => setTurnoverByFY((prev) => prev.filter((_, i) => i !== idx));

  // --- Ownership repeaters ---
  const handleOwnerChange = (idx, field, value) =>
    setOwners((prev) => prev.map((o, i) => (i === idx ? { ...o, [field]: value } : o)));
  // Fills BLANKS only — picking a person must never wipe a value already typed.
  const fillOwnerAt = (idx, patch) =>
    setOwners((prev) => prev.map((o, i) => {
      if (i !== idx) return o;
      const next = { ...o };
      for (const [k, v] of Object.entries(patch)) if (!next[k]) next[k] = v;
      return next;
    }));
  const addOwner = () => setOwners((prev) => [...prev, { ...emptyOwner }]);
  const removeOwner = (idx) => setOwners((prev) => prev.filter((_, i) => i !== idx));

  // A director who ALSO holds shares. The two lists stay separate — a director
  // is not a shareholder, and most are not — so ticking the box on the board
  // does not invent a holding: it starts a row in the shareholder list with the
  // identity already filled, leaving the SHARES to be entered where the
  // cap-table maths can see them. Never duplicates: somebody already on the
  // list by name or PAN is left alone.
  const addShareholderFromDirector = (member) => {
    const name = (member?.name || '').trim();
    if (!name) return;
    setOwners((prev) => {
      const already = prev.some((o) =>
        (o.pan && member.pan && o.pan.toUpperCase() === member.pan.toUpperCase())
        || o.name.trim().toLowerCase() === name.toLowerCase());
      if (already) return prev;
      return [...prev, {
        ...emptyOwner,
        holderType: 'individual',
        name,
        pan: (member.pan || '').toUpperCase(),
        din: member.din || '',
      }];
    });
  };

  const handleChargeChange = (idx, field, value) =>
    setCharges((prev) => prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
  const addCharge = () => setCharges((prev) => [...prev, { ...emptyCharge }]);
  const removeCharge = (idx) => setCharges((prev) => prev.filter((_, i) => i !== idx));

  const handleGroupRelChange = (idx, field, value) =>
    setGroupRelations((prev) => prev.map((g, i) => (i === idx ? { ...g, [field]: value } : g)));
  const addGroupRel = () => setGroupRelations((prev) => [...prev, { ...emptyGroupRelation }]);
  const removeGroupRel = (idx) => setGroupRelations((prev) => prev.filter((_, i) => i !== idx));

  // Owners/relations are stored with numbers & real dates; the form holds
  // strings. Drop blank rows, coerce, and strip empty optionals so we never PUT
  // "" where a Number/Date/ObjectId is expected. Shared by create and update.
  const cleanOwners = () =>
    owners
      .filter((o) => o.name.trim())
      .map((o) => {
        const out = {
          holderType: o.holderType,
          name: o.name.trim(),
          shareClass: o.shareClass,
          isUbo: !!o.isUbo,
          isSubscriber: !!o.isSubscriber,
        };
        if (o.holderClientId) out.holderClientId = o.holderClientId;
        if (o.pan?.trim()) out.pan = o.pan.trim().toUpperCase();
        if (o.din?.trim()) out.din = o.din.trim();
        if (o.aadhaar?.trim()) out.aadhaar = o.aadhaar.trim();
        if (o.sharesHeld !== '') out.sharesHeld = Number(o.sharesHeld);
        if (o.subscribedShares !== '') out.subscribedShares = Number(o.subscribedShares);
        if (o.ownershipPct !== '') out.ownershipPct = Number(o.ownershipPct);
        if (o.acquiredOn) out.acquiredOn = o.acquiredOn;
        if (o.disposedOn) out.disposedOn = o.disposedOn;
        return out;
      });

  // An address is only worth storing once SOMETHING has been typed into it.
  // An all-blank fieldset returns null, which clears a registered office that
  // was emptied on purpose rather than saving six empty strings.
  const ADDRESS_FIELDS = ['line1', 'line2', 'city', 'state', 'stateCode', 'pincode'];
  const cleanRegisteredAddress = () => {
    const filled = ADDRESS_FIELDS.some((f) => String(registeredAddress[f] || '').trim());
    return filled ? { ...registeredAddress } : null;
  };

  // A charge with no holder is not a charge. Everything else is optional: a
  // firm routinely records "HDFC, hypothecation, ₹50L" off the MCA index days
  // before it has the charge ID and the dates in front of it.
  const cleanCharges = () =>
    charges
      .filter((c) => c.holderName.trim())
      .map((c) => {
        const out = { holderName: c.holderName.trim(), chargeType: c.chargeType };
        if (c.chargeId?.trim()) out.chargeId = c.chargeId.trim();
        if (c.propertyDescription?.trim()) out.propertyDescription = c.propertyDescription.trim();
        if (c.notes?.trim()) out.notes = c.notes.trim();
        if (c.amount !== '') out.amount = Number(c.amount);
        // Sent as null rather than omitted: clearing a satisfaction date that
        // was entered by mistake has to be able to reopen the charge.
        out.createdOn = c.createdOn || null;
        out.modifiedOn = c.modifiedOn || null;
        out.satisfiedOn = c.satisfiedOn || null;
        return out;
      });

  const cleanGroupRelations = () =>
    groupRelations
      .filter((g) => g.childClientId)
      .map((g) => {
        const out = { childClientId: g.childClientId, relationType: g.relationType };
        if (g.ownershipPct !== '') out.ownershipPct = Number(g.ownershipPct);
        if (g.effectiveFrom) out.effectiveFrom = g.effectiveFrom;
        if (g.effectiveTo) out.effectiveTo = g.effectiveTo;
        return out;
      });

  // The only thing onboarding actually insists on. It lives here rather than
  // relying on the input's `required` because step 1's fields are UNMOUNTED on
  // step 2 — the browser cannot validate a field that isn't in the document, so
  // submitting from step 2 would sail past `required` and 400 at the server.
  const essentialsProblem = () => {
    if (!formData.displayName.trim()) return 'A display name is required — that is the only thing we need to create the client.';
    return '';
  };

  // A step change replaces the whole page, so the scroll position has to reset
  // or the user lands in the middle of the new one.
  const goToStep = (next) => {
    setStep(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToMoreDetails = () => {
    const problem = essentialsProblem();
    setError(problem);
    if (problem) return window.scrollTo({ top: 0, behavior: 'smooth' });
    goToStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Submitting from step 2 means step 1's inputs aren't mounted to be
    // validated — send the user back to the field rather than to a server error.
    const problem = essentialsProblem();
    setError(problem);
    if (problem) {
      if (!isEdit) goToStep(1);
      else window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setSaving(true);

    try {
      const payload = { ...formData };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === '') delete payload[k];
      });
      // Onboarding time is server-owned: never trust a client-supplied value.
      // On create the model's `default: Date.now()` stamps it with live server
      // time at insert; on edit we leave the stored value untouched. The form
      // field is display-only (fetched from /api/time).
      delete payload.onboardingDate;

      // Ownership & group structure go on both create and edit (full-array
      // replace). Normalise the scalars explicitly so a cleared parent/capital
      // is sent as null rather than silently dropped by the empty-strip above.
      payload.owners = cleanOwners();
      payload.groupRelations = cleanGroupRelations();
      payload.parentClientId = formData.parentClientId || null;
      payload.authorisedCapital = formData.authorisedCapital === '' ? null : Number(formData.authorisedCapital);
      payload.paidUpCapital = formData.paidUpCapital === '' ? null : Number(formData.paidUpCapital);

      // ── KYC, master data, the registered office and the charge register ──
      // Sent even when EMPTY, unlike the older text fields above, which the
      // blanket empty-strip means you can never blank out once set. A wrongly
      // typed Aadhaar or a company category picked by mistake has to be
      // clearable — for KYC data especially, "stuck at the wrong value" is the
      // worst of the three states.
      payload.aadhaar = formData.aadhaar || '';
      payload.lwf = formData.lwf || '';
      payload.vatTin = formData.vatTin || '';
      payload.rocCode = formData.rocCode || '';
      payload.nicCode = formData.nicCode || '';
      payload.businessActivity = formData.businessActivity || '';
      payload.companyCategory = formData.companyCategory || '';
      payload.companySubCategory = formData.companySubCategory || '';
      payload.dateOfBirth = formData.dateOfBirth || null;
      payload.dateOfIncorporation = formData.dateOfIncorporation || null;
      payload.registeredAddress = cleanRegisteredAddress();
      payload.charges = cleanCharges();

      // The photo is a pointer into the vault, so it only exists on the edit
      // path — on create the client has no documents to point at yet, and the
      // server ignores the field there anyway.
      if (isEdit) payload.photoDocumentId = formData.photoDocumentId || null;
      else delete payload.photoDocumentId;

      // Governance (Phase 2) — same full-array replace on both create and edit.
      const gov = cleanGovernance({ board, committees, statutoryOfficers });
      payload.board = gov.board;
      payload.committees = gov.committees;
      payload.statutoryOfficers = gov.statutoryOfficers;

      // Org chart (Phase 3) — same full-array replace on both create and edit.
      const oc = cleanOrgChart({ orgUnits, employees });
      payload.orgUnits = oc.orgUnits;
      payload.employees = oc.employees;

      if (isEdit) {
        await apiClient.put(`/api/clients/${id}`, payload);
        toast.success('Client updated');
        navigate(`/org-admin/clients/${id}`);
      } else {
        payload.contacts = contacts.filter((c) => c.name.trim());
        // Strip empty optional dates so we never POST "" where a Date is expected.
        payload.gstins = gstins
          .filter((g) => g.gstin.trim())
          .map((g) => {
            const clean = { ...g };
            if (!clean.registrationDate) delete clean.registrationDate;
            if (!clean.cancellationDate) delete clean.cancellationDate;
            return clean;
          });
        payload.bankAccounts = bankAccounts.filter((b) => b.accountNo.trim() && b.ifsc.trim());
        payload.signatories = signatories.filter((s) => s.name.trim());
        payload.turnoverByFY = turnoverByFY
          .filter((t) => t.fy.trim() && t.amount !== '')
          .map((t) => ({ fy: t.fy.trim(), amount: Number(t.amount) }));
        const res = await apiClient.post('/api/clients', payload);
        toast.success('Client created');
        navigate(`/org-admin/clients/${res.data._id}`);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <OrgAdminLayout title={isEdit ? 'Edit Client' : 'Add Client'} subtitle="">
        <Card><Skeleton.Text lines={5} /></Card>
      </OrgAdminLayout>
    );
  }

  // Which type-specific registration numbers apply.
  const isCompany = ['private_ltd', 'public_ltd'].includes(formData.clientType);
  const isLlp = formData.clientType === 'llp';
  // A natural person (or a business that IS one). Decides whether the record
  // asks for a date of birth and a KYC photograph, or for a date of formation
  // and the MCA master data — a proprietorship has a proprietor's Aadhaar, a
  // company has a CIN, and asking each for the other's fields is noise.
  const isNaturalPerson = ['individual', 'proprietorship', 'huf'].includes(formData.clientType);
  // Bodies that file charges with the RoC. A partnership firm or a trust
  // borrows too, but it does not appear on the MCA index of charges.
  const filesWithRoc = isCompany || isLlp;

  // Everyone already named anywhere on this client. The board, the signatory
  // list, the officer list and the cap table are mostly the SAME handful of
  // humans, and retyping a 10-character PAN is how one person becomes two.
  const people = collectClientPeople({
    contacts, signatories, board, statutoryOfficers, owners, employees,
  });

  // A GSTIN carries its holder's PAN in characters 3-12, so a client with any
  // GSTIN has already told us their PAN — asking again just invites a typo.
  // Offered, not forced: it fills only while the field is still empty.
  const derivedPan = panFromGstins(gstins);
  const panIsDerivable = !!derivedPan && !formData.pan;

  // The cap table as it currently stands, per share class — mirrors the model's
  // `ownershipByClass` virtual and the pre-validate rule that refuses >100%, so
  // the screen says what the server will do BEFORE the save is attempted.
  // Disposed holdings are excluded: a share sold is not part of today's table.
  const ownershipTotals = (() => {
    const byClass = new Map();
    for (const o of owners) {
      if (o.disposedOn || o.ownershipPct === '') continue;
      const pct = Number(o.ownershipPct);
      if (!Number.isFinite(pct)) continue;
      const cls = o.shareClass || 'equity';
      if (!byClass.has(cls)) byClass.set(cls, { shareClass: cls, holders: 0, total: 0 });
      const row = byClass.get(cls);
      row.holders += 1;
      row.total += pct;
    }
    // Float addition drifts (33.33 × 3 = 99.99000000000001); a cap table
    // reading 99.99000000000001% looks broken rather than incomplete.
    const r2 = (n) => Math.round(n * 100) / 100;
    return Array.from(byClass.values()).map((row) => {
      const total = r2(row.total);
      return {
        ...row,
        label: SHARE_CLASSES.find((s) => s.value === row.shareClass)?.label || row.shareClass,
        totalPct: total,
        unallocatedPct: r2(100 - total),
        // Same tolerance the model applies: three holders typing 33.34 make
        // 100.02, which is rounding, not an over-issue.
        over: total > 100.05,
        overBy: r2(total - 100),
        exact: Math.abs(total - 100) <= 0.05,
      };
    });
  })();

  return (
    <OrgAdminLayout
      title={isEdit ? 'Edit Client' : 'Add Client'}
      subtitle={
        isEdit
          ? 'Update client information'
          : step === 1
            ? 'The essentials — you can create the client from here'
            : 'Extra details — fill in whatever you have, or skip it entirely'
      }
    >
      <form onSubmit={handleSubmit} className="client-form">
        {!isEdit && (
          <ol className="onboard-steps" aria-label="Onboarding steps">
            <li className={step === 1 ? 'is-current' : 'is-done'}>
              <span className="onboard-step-n">1</span>
              <span className="onboard-step-t">Essentials</span>
            </li>
            <li className={step === 2 ? 'is-current' : ''}>
              <span className="onboard-step-n">2</span>
              <span className="onboard-step-t">More details <em>optional</em></span>
            </li>
          </ol>
        )}

        {error && <div className="form-error" role="alert">{error}</div>}

        {showEssentials && (
          <>
          <Card>
            <Card.Header title="Basic Information" />
            <div className="form-row">
              <Input label="Display Name *" name="displayName" value={formData.displayName} onChange={handleChange} required />
              <Input label="Legal Name" name="legalName" value={formData.legalName} onChange={handleChange} />
            </div>
            <div className="form-row">
              <Select
                label="Client Type *"
                name="clientType"
                value={formData.clientType}
                onChange={handleChange}
                required
                disabled={isEdit}
                helperText={isEdit ? 'Client type is fixed after creation' : undefined}
              >
                {CLIENT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </Select>
              {isEdit && (
                <Select label="Status" name="status" value={formData.status} onChange={handleChange}>
                  <option value="onboarding">Onboarding</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="closed">Closed</option>
                </Select>
              )}
              <Input
                label="Onboarding Date"
                type="datetime-local"
                name="onboardingDate"
                value={formData.onboardingDate}
                onChange={handleChange}
                readOnly
              />
            </div>

            {/* ⚠️ NOT THE SAME DATE AS ONBOARDING, which is only when the firm
                took them on. Incorporation decides the first-year audit
                exemption, the first AGM due date and the first ROC filing, and
                it is read off the certificate of incorporation. */}
            {!isNaturalPerson && (
              <div className="form-row">
                <Input
                  label={isCompany ? 'Date of Incorporation' : 'Date of Formation'}
                  type="date"
                  name="dateOfIncorporation"
                  value={formData.dateOfIncorporation}
                  onChange={handleChange}
                  helperText="From the COI / deed — not the date you took them on"
                />
              </div>
            )}

            {/* KYC. Every pack an Indian practice assembles asks for the same
                five things — PAN, Aadhaar, mobile, email, photograph. PAN is
                under Registrations; mobile and email are on Contacts, which is
                already where the rest of the app reads them from. */}
            {isNaturalPerson && (
              <div className="form-row">
                <Input
                  label="Aadhaar"
                  name="aadhaar"
                  value={formData.aadhaar}
                  onChange={(e) => setFormData((prev) => ({ ...prev, aadhaar: e.target.value.replace(/\D/g, '') }))}
                  maxLength={12}
                  inputMode="numeric"
                  placeholder="12 digits"
                />
                <Input
                  label="Date of Birth"
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                />
                {/* A pointer into the vault, never a second upload path: the
                    photograph is an ordinary Document with the same storage,
                    backup and access log as everything else the firm holds. */}
                {isEdit && (
                  <Select
                    label="KYC Photograph"
                    name="photoDocumentId"
                    value={formData.photoDocumentId}
                    onChange={handleChange}
                    helperText={
                      photoDocs.length
                        ? 'Chosen from this client’s uploaded documents'
                        : 'Upload one under Documents (category: Photograph) first'
                    }
                  >
                    <option value="">— None —</option>
                    {photoDocs.map((d) => (
                      <option key={d.id} value={d.id}>{d.title}</option>
                    ))}
                  </Select>
                )}
              </div>
            )}
          </Card>
  
          <Card>
            <Card.Header title="Registrations" />
            <div className="form-row">
              {/* PAN is DERIVABLE from any GSTIN on file (characters 3-12), so
                  it is offered rather than demanded. Offered, not auto-applied:
                  silently writing a statutory identifier the user never typed
                  is not something to do without them seeing it happen. */}
              <Input
                label="PAN"
                name="pan"
                value={formData.pan}
                onChange={handleChange}
                maxLength={10}
                helperText={panIsDerivable ? `From this client's GSTIN: ${derivedPan}` : undefined}
                iconRight={panIsDerivable ? (
                  <button
                    type="button"
                    className="gst-link"
                    onClick={() => setFormData((prev) => ({ ...prev, pan: derivedPan }))}
                  >
                    Use
                  </button>
                ) : undefined}
              />
              <Input label="TAN" name="tan" value={formData.tan} onChange={handleChange} />
              {/* CIN applies to companies, LLPIN to LLPs — show only the relevant one. */}
              {isCompany && <Input label="CIN" name="cin" value={formData.cin} onChange={handleChange} />}
              {isLlp && <Input label="LLPIN" name="llpin" value={formData.llpin} onChange={handleChange} />}
            </div>
  
            {/* GSTINs are part of a client's registrations. Multi-entry (per state)
                with full metadata; managed here on create, on the detail page in edit. */}
            {!isEdit && (
              <div className="gstins-subsection">
                <div className="apob-header">
                  <span className="ds-field-label">GSTINs</span>
                  <div className="gstin-actions">
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      onClick={() => setGstFetchOpen(true)}
                      disabled={gstFetchOpen}
                    >
                      ⇩ Fetch from GST Portal
                    </Button>
                    <Button type="button" variant="secondary" size="sm" onClick={addGstin}>+ Add manually</Button>
                  </div>
                </div>
  
                {gstFetchOpen && (
                  <GstPortalFetch
                    onFetched={handleGstFetched}
                    onCancel={() => setGstFetchOpen(false)}
                  />
                )}
  
                {gstins.length === 0 && !gstFetchOpen && (
                  <p className="hint-text">
                    No GSTINs added yet — fetch one from the GST portal to auto-fill it, or add it manually.
                  </p>
                )}
                {gstins.map((gstin, idx) => (
                  <div key={idx} className="gstin-edit-block">
                    <div className="gstin-edit-head">
                      <span className="ds-field-label">GSTIN {idx + 1}</span>
                      <button type="button" onClick={() => removeGstin(idx)} className="btn-remove-icon">×</button>
                    </div>
                    <GstinFields value={gstin} onChange={(next) => setGstinAt(idx, next)} showArnFields={false} />
                  </div>
                ))}
              </div>
            )}

            {/* EDIT mode gets the full manager rather than the staged repeater
                above. It writes straight to the API (like the auditors,
                credentials and documents managers further down), which is what
                lets it offer the portal FETCH and the per-GSTIN REFRESH — both
                need a client that already exists. The lookup is deliberately
                NOT an onboarding-only tool: a registration read once at signup
                goes stale, and a GSTIN moving to Cancelled is exactly what the
                firm needs to notice. */}
            {isEdit && (
              <div className="gstins-subsection">
                <GstinManager
                  clientId={id}
                  gstins={gstins}
                  onChange={setGstins}
                  client={formData}
                  onClientPatched={(c) => setFormData((prev) => ({
                    ...prev,
                    legalName: c.legalName || prev.legalName,
                    pan: c.pan || prev.pan,
                  }))}
                />
              </div>
            )}
          </Card>
  
          {/* ⚠️ THE ONLY ADDRESS THE CLIENT ITSELF HAS. Before this an address
              could exist only INSIDE a GSTIN, so an individual, a dormant
              company or a trust with no GST registration had nowhere to put
              one, and a registered office had to be faked as a place of
              business. They are different things: the registered office is
              where the RoC serves notice, and it is routinely the auditor's
              premises rather than anywhere the business operates from. */}
          <Card>
            <Card.Header
              title={isNaturalPerson ? 'Address' : 'Registered Office'}
              subtitle={
                isNaturalPerson
                  ? 'The address on the KYC record'
                  : 'Where the RoC serves notice — separate from any GST place of business'
              }
            />
            <AddressFields
              value={registeredAddress}
              onChange={setRegisteredAddress}
              legend={isNaturalPerson ? 'Address' : 'Registered office'}
            />
          </Card>

          <Card>
            <Card.Header title="Other Registrations & Licenses" />
            <div className="form-row">
              <Input label="IEC" name="iec" value={formData.iec} onChange={handleChange} />
              <Input label="Udyam" name="udyam" value={formData.udyam} onChange={handleChange} />
              <Input label="Professional Tax" name="professionalTax" value={formData.professionalTax} onChange={handleChange} />
            </div>
            <div className="form-row">
              <Input label="PF Number" name="pf" value={formData.pf} onChange={handleChange} />
              <Input label="ESIC Number" name="esic" value={formData.esic} onChange={handleChange} />
              {/* The third payroll registration, and the one that was missing.
                  A separate state registration from PF and ESIC, with its own
                  half-yearly remittance. */}
              <Input label="LWF Number" name="lwf" value={formData.lwf} onChange={handleChange} helperText="Labour Welfare Fund" />
            </div>
            <div className="form-row">
              {/* Pre-GST. Kept because assessments, appeals and refunds under
                  the old regime run for years after the tax itself is gone,
                  and the TIN is the only key to that file. */}
              <Input
                label="VAT / CST TIN"
                name="vatTin"
                value={formData.vatTin}
                onChange={(e) => setFormData((prev) => ({ ...prev, vatTin: e.target.value.toUpperCase() }))}
                helperText="Legacy pre-GST registration, for old assessments"
              />
            </div>
          </Card>
  
          <Card>
            <Card.Header title="Assignment" />
            <div className="form-row">
              <Select label="Assigned Partner" name="assignedPartnerId" value={formData.assignedPartnerId} onChange={handleChange}>
                <option value="">-- None --</option>
                {orgUsers.map((u) => (
                  <option key={u._id} value={u._id}>{u.name} ({u.role?.name})</option>
                ))}
              </Select>
              <Select label="Assigned Manager" name="assignedManagerId" value={formData.assignedManagerId} onChange={handleChange}>
                <option value="">-- None --</option>
                {orgUsers.map((u) => (
                  <option key={u._id} value={u._id}>{u.name} ({u.role?.name})</option>
                ))}
              </Select>
            </div>
          </Card>
          </>
        )}

        {showMoreDetails && (
          <>
          {/* Ownership & group structure (Phase 1). Shown in both modes — a
              client's cap table and group tree change over its life, so unlike the
              create-only repeaters below this must stay editable. */}
          <Card>
            <Card.Header title="Ownership & Group Structure" />
            <div className="form-row">
              <Input label="Authorised Capital (₹)" type="number" min="0" name="authorisedCapital" value={formData.authorisedCapital} onChange={handleChange} />
              <Input label="Paid-up Capital (₹)" type="number" min="0" name="paidUpCapital" value={formData.paidUpCapital} onChange={handleChange} />
              <Select label="Parent Company" name="parentClientId" value={formData.parentClientId} onChange={handleChange}>
                <option value="">-- None (top of group) --</option>
                {otherClients.map((c) => (
                  <option key={c._id} value={c._id}>{c.displayName}</option>
                ))}
              </Select>
            </div>
  
            <div className="gstins-subsection">
              <div className="apob-header">
                <span className="ds-field-label">Shareholders / Members / Partners</span>
                <Button type="button" variant="secondary" size="sm" onClick={addOwner}>+ Add Shareholder</Button>
              </div>
              {owners.length === 0 && (
                <p className="hint-text">
                  No shareholders recorded yet. This list is SEPARATE from the board below — a
                  director may hold no shares at all. Add shareholders, partners, members or
                  trustees, and flag any Ultimate Beneficial Owner (≥25% is flagged automatically).
                </p>
              )}

              {/* ⚠️ The cap-table check, per SHARE CLASS. Equity and preference
                  are separate pools, so one grand total would read as 200% for
                  a company holding both. Over 100% is refused by the server;
                  under is normal and only reported, because a firm rarely has
                  every shareholder on file at once. */}
              {ownershipTotals.length > 0 && (
                <div className="own-totals">
                  {ownershipTotals.map((t) => (
                    <span key={t.shareClass} className={`own-total ${t.over ? 'is-over' : t.exact ? 'is-exact' : ''}`}>
                      <b>{t.label}</b> {t.totalPct}% across {t.holders} holder{t.holders === 1 ? '' : 's'}
                      {t.over
                        ? ` — over by ${t.overBy}%`
                        : t.exact
                          ? ' — fully allocated'
                          : ` — ${t.unallocatedPct}% unallocated`}
                    </span>
                  ))}
                </div>
              )}
              {owners.map((o, idx) => (
                <div key={idx} className="gstin-edit-block">
                  <div className="gstin-edit-head">
                    <span className="ds-field-label">Owner {idx + 1}</span>
                    <button type="button" onClick={() => removeOwner(idx)} className="btn-remove-icon">×</button>
                  </div>
                  {/* A shareholder is very often a director already on file —
                      offer them rather than making somebody retype the PAN. */}
                  {o.holderType === 'individual' && (
                    <div className="form-row">
                      <PersonPicker people={people} onPick={(patch) => fillOwnerAt(idx, patch)} fields={['name', 'pan', 'din', 'aadhaar']} />
                    </div>
                  )}
                  <div className="form-row">
                    <Select label="Holder Type" value={o.holderType} onChange={(e) => handleOwnerChange(idx, 'holderType', e.target.value)}>
                      {HOLDER_TYPES.map((h) => <option key={h.value} value={h.value}>{h.label}</option>)}
                    </Select>
                    <Input label="Name" value={o.name} onChange={(e) => handleOwnerChange(idx, 'name', e.target.value)} />
                    {/* A corporate/trust/fund holder may itself be a client we track —
                        linking it is the edge UBO resolution walks up the chain. */}
                    {o.holderType !== 'individual' && (
                      <Select label="Linked Client (if tracked)" value={o.holderClientId} onChange={(e) => handleOwnerChange(idx, 'holderClientId', e.target.value)}>
                        <option value="">-- Not tracked --</option>
                        {otherClients.map((c) => <option key={c._id} value={c._id}>{c.displayName}</option>)}
                      </Select>
                    )}
                  </div>
                  <div className="form-row">
                    <Select label="Share Class" value={o.shareClass} onChange={(e) => handleOwnerChange(idx, 'shareClass', e.target.value)}>
                      {SHARE_CLASSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </Select>
                    {/* Shares are whole; a percentage is not. */}
                    <Input label="Shares Held" type="number" min="0" step="1" value={o.sharesHeld} onChange={(e) => handleOwnerChange(idx, 'sharesHeld', e.target.value)} />
                    {/* ⚠️ step="any". A number input defaults to step=1, and the
                        browser then marks 33.33 as invalid — which is most of a
                        real cap table. Voting % is gone; see the model. */}
                    <Input
                      label="Ownership %"
                      type="number"
                      min="0"
                      max="100"
                      step="any"
                      inputMode="decimal"
                      placeholder="e.g. 33.33"
                      value={o.ownershipPct}
                      onChange={(e) => handleOwnerChange(idx, 'ownershipPct', e.target.value)}
                    />
                  </div>
                  <div className="form-row">
                    <Input label="PAN" value={o.pan} onChange={(e) => handleOwnerChange(idx, 'pan', e.target.value.toUpperCase())} maxLength={10} />
                    <Input label="DIN" value={o.din} onChange={(e) => handleOwnerChange(idx, 'din', e.target.value)} maxLength={8} />
                    {/* A corporate holder has a PAN but no Aadhaar, so the
                        field follows the holder type rather than sitting there
                        inviting somebody to put a company's PAN in it. */}
                    {o.holderType === 'individual' && (
                      <Input
                        label="Aadhaar"
                        value={o.aadhaar}
                        onChange={(e) => handleOwnerChange(idx, 'aadhaar', e.target.value.replace(/\D/g, ''))}
                        maxLength={12}
                        inputMode="numeric"
                        placeholder="12 digits"
                      />
                    )}
                    <Input label="Acquired On" type="date" value={o.acquiredOn} onChange={(e) => handleOwnerChange(idx, 'acquiredOn', e.target.value)} />
                    <Input label="Disposed On" type="date" value={o.disposedOn} onChange={(e) => handleOwnerChange(idx, 'disposedOn', e.target.value)} />
                  </div>
                  {/* ── The MOA subscriber sheet ─────────────────────────────
                      A flag on the HOLDING, not a second list: a subscriber is
                      a shareholder like any other, and a parallel subscribers[]
                      would be the same person maintained twice with nothing
                      forcing the two to agree.

                      ⚠️ Subscribed ≠ held. The first is frozen at
                      incorporation; the second is today. A subscriber who has
                      since sold out keeps their subscribed shares and holds
                      nothing — which is exactly what "who subscribed to the
                      memorandum?" is asking. */}
                  <div className="form-row">
                    <label className="checkbox-label">
                      <input type="checkbox" checked={o.isSubscriber} onChange={(e) => handleOwnerChange(idx, 'isSubscriber', e.target.checked)} />
                      Subscriber to the MOA (signed at incorporation)
                    </label>
                    {o.isSubscriber && (
                      <Input
                        label="Shares Subscribed"
                        type="number"
                        min="0"
                        step="1"
                        value={o.subscribedShares}
                        onChange={(e) => handleOwnerChange(idx, 'subscribedShares', e.target.value)}
                        helperText="From the subscriber sheet — not today's holding"
                      />
                    )}
                  </div>
                  <label className="checkbox-label">
                    <input type="checkbox" checked={o.isUbo} onChange={(e) => handleOwnerChange(idx, 'isUbo', e.target.checked)} />
                    Ultimate Beneficial Owner (UBO)
                  </label>
                </div>
              ))}
            </div>
  
            <div className="gstins-subsection">
              <div className="apob-header">
                <span className="ds-field-label">Group Companies (subsidiaries, JVs, branches)</span>
                <Button type="button" variant="secondary" size="sm" onClick={addGroupRel} disabled={otherClients.length === 0}>+ Add Relationship</Button>
              </div>
              {otherClients.length === 0 ? (
                <p className="hint-text">Group relationships link to other clients in your org — add another client first.</p>
              ) : groupRelations.length === 0 && (
                <p className="hint-text">Link the companies this client holds — each with the relationship type and stake.</p>
              )}
              {groupRelations.map((g, idx) => (
                <div key={idx} className="gstin-edit-block">
                  <div className="gstin-edit-head">
                    <span className="ds-field-label">Relationship {idx + 1}</span>
                    <button type="button" onClick={() => removeGroupRel(idx)} className="btn-remove-icon">×</button>
                  </div>
                  <div className="form-row">
                    <Select label="Company" value={g.childClientId} onChange={(e) => handleGroupRelChange(idx, 'childClientId', e.target.value)}>
                      <option value="">-- Select client --</option>
                      {otherClients.map((c) => <option key={c._id} value={c._id}>{c.displayName}</option>)}
                    </Select>
                    <Select label="Relation" value={g.relationType} onChange={(e) => handleGroupRelChange(idx, 'relationType', e.target.value)}>
                      {GROUP_RELATION_TYPES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </Select>
                    <Input label="Ownership %" type="number" min="0" max="100" value={g.ownershipPct} onChange={(e) => handleGroupRelChange(idx, 'ownershipPct', e.target.value)} />
                  </div>
                  <div className="form-row">
                    <Input label="Effective From" type="date" value={g.effectiveFrom} onChange={(e) => handleGroupRelChange(idx, 'effectiveFrom', e.target.value)} />
                    <Input label="Effective To" type="date" value={g.effectiveTo} onChange={(e) => handleGroupRelChange(idx, 'effectiveTo', e.target.value)} />
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* MCA master data. `clientType` already says private vs public;
              these say how the liability is limited, who owns it, which
              registrar holds the file and what the company actually does —
              which is what MGT-7 and most registration applications ask for,
              and what a preparer otherwise re-reads off the portal each time. */}
          {filesWithRoc && (
            <Card>
              <Card.Header
                title="MCA Master Data"
                subtitle="Straight off the company master — category, registrar and principal activity"
              />
              {isCompany && (
                <div className="form-row">
                  <Select label="Company Category" name="companyCategory" value={formData.companyCategory} onChange={handleChange}>
                    <option value="">— Not recorded —</option>
                    {COMPANY_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </Select>
                  <Select label="Company Sub-category" name="companySubCategory" value={formData.companySubCategory} onChange={handleChange}>
                    <option value="">— Not recorded —</option>
                    {COMPANY_SUB_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </Select>
                </div>
              )}
              <div className="form-row">
                <Input label="RoC" name="rocCode" value={formData.rocCode} onChange={handleChange} placeholder="e.g. RoC-Mumbai" />
                <Input label="NIC Code" name="nicCode" value={formData.nicCode} onChange={handleChange} placeholder="e.g. 62011" helperText="Principal business activity code" />
                <Input label="Business Activity" name="businessActivity" value={formData.businessActivity} onChange={handleChange} placeholder="e.g. Computer programming" />
              </div>
            </Card>
          )}

          {/* ── Register of charges ──────────────────────────────────────────
              Every mortgage, hypothecation or lien registered against the
              client with the RoC. A charge outliving its loan is the classic
              due-diligence finding: the bank was repaid years ago, CHG-4 was
              never filed, and the charge is still open on the MCA index — so
              the register is worth keeping where somebody will see it.

              ⚠️ Open vs satisfied is DERIVED from the satisfaction date, never
              typed. A status somebody has to remember to flip is a status that
              quietly lies the first time they forget. */}
          {filesWithRoc && (
            <Card>
              <Card.Header
                title="Registered Charges"
                subtitle="Charges created with the RoC (CHG-1) and satisfied (CHG-4)"
                action={<Button type="button" variant="secondary" size="sm" onClick={addCharge}>+ Add Charge</Button>}
              />
              {charges.length === 0 && (
                <p className="hint-text">
                  No charges recorded. Add each mortgage, hypothecation or lien on the MCA
                  index — a charge stays OPEN until a satisfaction date is entered, which is
                  how one that was repaid but never closed becomes visible.
                </p>
              )}
              {charges.map((c, idx) => (
                <div key={idx} className="gstin-edit-block">
                  <div className="gstin-edit-head">
                    <span className="ds-field-label">
                      Charge {idx + 1}
                      {c.satisfiedOn ? ' — satisfied' : ' — open'}
                    </span>
                    <button type="button" onClick={() => removeCharge(idx)} className="btn-remove-icon">×</button>
                  </div>
                  <div className="form-row">
                    <Input label="Charge Holder" value={c.holderName} onChange={(e) => handleChargeChange(idx, 'holderName', e.target.value)} placeholder="Bank / FI / debenture trustee" />
                    <Select label="Charge Type" value={c.chargeType} onChange={(e) => handleChargeChange(idx, 'chargeType', e.target.value)}>
                      {CHARGE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </Select>
                    <Input label="Amount (₹)" type="number" min="0" value={c.amount} onChange={(e) => handleChargeChange(idx, 'amount', e.target.value)} />
                    <Input label="Charge ID" value={c.chargeId} onChange={(e) => handleChargeChange(idx, 'chargeId', e.target.value)} placeholder="MCA charge ID" />
                  </div>
                  <div className="form-row">
                    <Input label="Created On" type="date" value={c.createdOn} onChange={(e) => handleChargeChange(idx, 'createdOn', e.target.value)} />
                    <Input label="Last Modified On" type="date" value={c.modifiedOn} onChange={(e) => handleChargeChange(idx, 'modifiedOn', e.target.value)} />
                    <Input
                      label="Satisfied On"
                      type="date"
                      value={c.satisfiedOn}
                      onChange={(e) => handleChargeChange(idx, 'satisfiedOn', e.target.value)}
                      helperText="Leave blank while the charge is still open"
                    />
                  </div>
                  <div className="form-row">
                    <Input label="Property Charged" value={c.propertyDescription} onChange={(e) => handleChargeChange(idx, 'propertyDescription', e.target.value)} placeholder="e.g. Book debts and stock in trade" />
                    <Input label="Notes" value={c.notes} onChange={(e) => handleChargeChange(idx, 'notes', e.target.value)} />
                  </div>
                </div>
              ))}
            </Card>
          )}
  
          {/* Governance (Phase 2). Board, committees and statutory officers — also
              shown in both modes, since these turn over during a company's life. */}
          <Card>
            <Card.Header title="Governance" />
            <GovernanceFields
              board={board}
              committees={committees}
              statutoryOfficers={statutoryOfficers}
              onBoardChange={setBoard}
              onCommitteesChange={setCommittees}
              onOfficersChange={setStatutoryOfficers}
              onAddShareholder={addShareholderFromDirector}
              people={people}
            />
          </Card>
  
          {/* Management / org chart (Phase 3). Units + reporting lines, both modes. */}
          <Card>
            <Card.Header title="Organisation Chart" />
            <OrgChartFields
              orgUnits={orgUnits}
              employees={employees}
              onUnitsChange={setOrgUnits}
              onEmployeesChange={setEmployees}
            />
          </Card>
  
          {/* Edit only: an appointment is written straight to the server (it
              consumes one of the auditor's audit slots and is checked against
              their ceiling), which needs a client that already exists. */}
          {isEdit && (
            <ClientAuditorsManager
              clientId={id}
              appointments={appointments}
              onChange={setAppointments}
            />
          )}
  
          {/* Edit only: credentials belong to a client that already exists (they're
              written straight to the vault, not staged in the form). */}
          {isEdit && <CredentialsManager clientId={id} />}
  
          {/* Edit only: the client's document vault (uploaded straight to storage). */}
          {isEdit && <DocumentsManager clientId={id} />}
  
          {/* Edit only: who at this client can sign in to the portal. Passed the
              saved contacts so a login can be created from an existing contact
              rather than retyping (and misspelling) their details. */}
          {isEdit && <ClientPortalUsers clientId={id} contacts={contacts} />}
  
          {/* Edit only, and saved separately from the form: the suggestion list is
              derived server-side from the SAVED contacts and portal logins, so it
              needs a client that already exists. */}
          {isEdit && <BillingRecipients clientId={id} />}
  
          {!isEdit && (
            <>
              <Card>
                <Card.Header
                  title="Contacts"
                  action={<Button type="button" variant="secondary" size="sm" onClick={addContact}>+ Add Contact</Button>}
                />
                {contacts.map((contact, idx) => (
                  <div key={idx} className="repeater-row">
                    <input className="ds-input" placeholder="Name" value={contact.name} onChange={(e) => handleContactChange(idx, 'name', e.target.value)} />
                    <input className="ds-input" placeholder="Role" value={contact.role} onChange={(e) => handleContactChange(idx, 'role', e.target.value)} />
                    <input className="ds-input" placeholder="Email" value={contact.email} onChange={(e) => handleContactChange(idx, 'email', e.target.value)} />
                    <input className="ds-input" placeholder="Phone" value={contact.phone} onChange={(e) => handleContactChange(idx, 'phone', e.target.value)} />
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={contact.isPrimary}
                        onChange={(e) => handleContactChange(idx, 'isPrimary', e.target.checked)}
                      />
                      Primary
                    </label>
                    {contacts.length > 1 && (
                      <button type="button" onClick={() => removeContact(idx)} className="btn-remove-icon">×</button>
                    )}
                  </div>
                ))}
              </Card>
  
              <Card>
                <Card.Header
                  title="Bank Accounts"
                  action={<Button type="button" variant="secondary" size="sm" onClick={addBank}>+ Add Account</Button>}
                />
                {bankAccounts.length === 0 && <p className="hint-text">No bank accounts added yet.</p>}
                {bankAccounts.map((b, idx) => (
                  <div key={idx} className="repeater-row">
                    <input className="ds-input" placeholder="Account No" value={b.accountNo} onChange={(e) => handleBankChange(idx, 'accountNo', e.target.value)} />
                    <input className="ds-input" placeholder="IFSC" value={b.ifsc} onChange={(e) => handleBankChange(idx, 'ifsc', e.target.value.toUpperCase())} maxLength={11} />
                    <input className="ds-input" placeholder="Bank" value={b.bankName} onChange={(e) => handleBankChange(idx, 'bankName', e.target.value)} />
                    <input className="ds-input" placeholder="Branch" value={b.branch} onChange={(e) => handleBankChange(idx, 'branch', e.target.value)} />
                    <select className="ds-select" value={b.accountType} onChange={(e) => handleBankChange(idx, 'accountType', e.target.value)}>
                      <option value="savings">Savings</option>
                      <option value="current">Current</option>
                      <option value="cc">Cash Credit</option>
                      <option value="od">Overdraft</option>
                    </select>
                    <label className="checkbox-label">
                      <input type="checkbox" checked={b.isPrimary} onChange={(e) => handleBankChange(idx, 'isPrimary', e.target.checked)} />
                      Primary
                    </label>
                    <button type="button" onClick={() => removeBank(idx)} className="btn-remove-icon">×</button>
                  </div>
                ))}
              </Card>
  
              <Card>
                <Card.Header
                  title="Signatories / Directors"
                  action={<Button type="button" variant="secondary" size="sm" onClick={addSignatory}>+ Add Signatory</Button>}
                />
                {signatories.length === 0 && <p className="hint-text">Add directors, partners, or authorized signatories. DSCs are managed on the client page after saving.</p>}
                {/* This form's own people list. GovernanceFields declares a
                    separate one under its own useId — two components must never
                    hard-code the same datalist id, or the document is invalid
                    the moment both render and one list silently wins. */}
                <datalist id={signatoryPeopleListId}>
                  {people.map((p) => <option key={p.key} value={p.name} />)}
                </datalist>
                {signatories.map((s, idx) => (
                  <div key={idx} className="repeater-row">
                    {/* Signatories are almost always directors already on the
                        board. Typing the name picks up their PAN and DIN too,
                        so the identity comes from one place — a datalist rather
                        than a second control, since this row is already tight. */}
                    <input
                      className="ds-input"
                      placeholder="Name"
                      list={signatoryPeopleListId}
                      value={s.name}
                      onChange={(e) => {
                        const name = e.target.value;
                        const hit = people.find((p) => p.name === name);
                        setSignatories((prev) => prev.map((row, i) => {
                          if (i !== idx) return row;
                          const next = { ...row, name };
                          // Blanks only — never overwrite what is already typed.
                          if (hit) {
                            if (!next.pan && hit.pan) next.pan = hit.pan;
                            if (!next.din && hit.din) next.din = hit.din;
                            if (!next.role && hit.designation) next.role = hit.designation;
                          }
                          return next;
                        }));
                      }}
                    />
                    <input className="ds-input" placeholder="Role" value={s.role} onChange={(e) => handleSignatoryChange(idx, 'role', e.target.value)} />
                    <input className="ds-input" placeholder="DIN" value={s.din} onChange={(e) => handleSignatoryChange(idx, 'din', e.target.value)} maxLength={8} />
                    <input className="ds-input" placeholder="PAN" value={s.pan} onChange={(e) => handleSignatoryChange(idx, 'pan', e.target.value.toUpperCase())} maxLength={10} />
                    <input className="ds-input" placeholder="Aadhaar" value={s.aadhaar} onChange={(e) => handleSignatoryChange(idx, 'aadhaar', e.target.value)} maxLength={12} />
                    <button type="button" onClick={() => removeSignatory(idx)} className="btn-remove-icon">×</button>
                  </div>
                ))}
              </Card>
  
              <Card>
                <Card.Header
                  title="Turnover by Financial Year"
                  action={<Button type="button" variant="secondary" size="sm" onClick={addTurnover}>+ Add Year</Button>}
                />
                {turnoverByFY.length === 0 && <p className="hint-text">Add turnover to derive audit applicability (44AB / GST audit / statutory).</p>}
                {turnoverByFY.map((t, idx) => (
                  <div key={idx} className="repeater-row">
                    <input className="ds-input" placeholder="FY (2024-25)" value={t.fy} onChange={(e) => handleTurnoverChange(idx, 'fy', e.target.value)} />
                    <input className="ds-input" type="number" min="0" placeholder="Turnover (₹)" value={t.amount} onChange={(e) => handleTurnoverChange(idx, 'amount', e.target.value)} />
                    <button type="button" onClick={() => removeTurnover(idx)} className="btn-remove-icon">×</button>
                  </div>
                ))}
              </Card>
            </>
          )}
          </>
        )}

        <div className="form-actions">
          {/* Create is the primary action on BOTH steps — step 2 is optional,
              so it must never look like a gate the user has to get through. */}
          <Button type="submit" variant="primary" loading={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Client'}
          </Button>
          {!isEdit && step === 1 && (
            <Button type="button" variant="secondary" onClick={goToMoreDetails}>
              More details about client →
            </Button>
          )}
          {!isEdit && step === 2 && (
            <Button type="button" variant="secondary" onClick={() => goToStep(1)}>
              ← Back
            </Button>
          )}
          <Button type="button" variant="secondary" onClick={() => navigate(-1)}>Cancel</Button>
        </div>

        {!isEdit && step === 2 && (
          <p className="hint-text onboard-optional-note">
            Nothing on this page is required. Leave anything you don't have — it can all be added
            from the client's page later.
          </p>
        )}
      </form>
    </OrgAdminLayout>
  );
}

export default CreateEditClient;
